import { Injectable } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { RouterStateSnapshot, TitleStrategy } from "@angular/router";
import { TranslocoService } from "@jsverse/transloco";
import { take } from "rxjs";
import { BrandService } from "./brand.service";

/**
 * Reads `title` from each route, treats it as a Transloco translation key, translates it, and prefixes with the brand product name (e.g. "RAN Connect - Settings").
 *
 * Routes declare titles via the standard Angular `title` property:
 * ```
 * { path: 'settings', component: SettingsPage, title: 'app.organization-settings.title' }
 * ```
 *
 * Pages that need to render a fully dynamic title (e.g. based on a loaded entity name) can still inject and call Angular's `Title` service directly — this strategy only handles the route-driven case.
 */
@Injectable({ providedIn: "root" })
export class BrandTitleStrategy extends TitleStrategy {
  constructor(
    private readonly title: Title,
    private readonly transloco: TranslocoService,
    private readonly brandService: BrandService,
  ) {
    super();
  }

  /**
   * t(app.organization-selection.title)
   * t(app.error.generic.title)
   * t(app.error.notFound.title)
   * t(app.process-list.title)
   * t(app.sign-in.title)
   * t(app.sign-up.title)
   * t(app.sign-in-redirect.title)
   * t(app.transaction-overview.title)
   * t(app.organization-home-page.title)
   * t(app.organization-info.title)
   * t(app.organization-settings.title)
   * t(app.organization-audit-log.title)
   * t(app.listTransactions.title)
   * t(app.uploadedFiles.title)
   * t(admin.home.title)
   * t(admin.processes.title)
   * t(admin.process-details.title)
   * t(admin.features.title)
   * t(admin.feature-details.title)
   * t(admin.services.title)
   * t(admin.service-details.title)
   * t(admin.organizations.title)
   * t(admin.users.title)
   * t(admin.user-details.title)
   */
  override updateTitle(snapshot: RouterStateSnapshot): void {
    const titleKey = this.buildTitle(snapshot);
    const productName = this.brandService.brand().productName;

    if (!titleKey) {
      this.title.setTitle(productName);
      return;
    }

    // The deepest route's data (resolved values included) is passed as interpolation params, so a title key like
    // "Service: {{serviceTitle}}" can name the entity the page just loaded.
    this.transloco
      .selectTranslate(titleKey, this._deepestRouteData(snapshot))
      .pipe(take(1))
      .subscribe((translated) => {
        this.title.setTitle(`${productName} - ${translated}`);
      });
  }

  private _deepestRouteData(snapshot: RouterStateSnapshot): Record<string, unknown> {
    let route = snapshot.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.data;
  }
}
